/**
 * WorkOrderCategoryManagement — Admin
 * Gestion des catégories de bons de travaux.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Hammer, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import {
  getWorkOrderCategories,
  createWorkOrderCategory,
  updateWorkOrderCategory,
  deleteWorkOrderCategory,
} from '../../api';

const COLORS = [
  '#06b6d4', '#eab308', '#78716c', '#f43f5e', '#f97316',
  '#10b981', '#6366f1', '#0ea5e9', '#ef4444', '#6b7280',
];

const WorkOrderCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', icon: '', color: COLORS[0] });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ name: '', icon: '', color: '' });
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWorkOrderCategories();
      setCategories(res.data);
      setError(null);
    } catch (e) {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    try {
      await createWorkOrderCategory(formData);
      setFormData({ name: '', icon: '', color: COLORS[0] });
      setShowForm(false);
      fetchCategories();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de création');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await updateWorkOrderCategory(id, editData);
      setEditId(null);
      fetchCategories();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de modification');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await deleteWorkOrderCategory(id);
      fetchCategories();
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de suppression');
    }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    setEditData({ name: cat.name, icon: cat.icon || '', color: cat.color || '' });
  };

  const inputCls = 'border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30';

  return (
<div className="px-4 sm:px-6 lg:px-8 space-y-6"><Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2"><Hammer size={16} className="text-primary" />Catégories Travaux</span>
          <button onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 text-sm bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary-dark transition">
            <Plus size={14} />Ajouter
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{error}</div>
        )}

        {showForm && (
          <form onSubmit={handleCreate} className="flex gap-2 flex-wrap mb-4 p-3 bg-gray-50 rounded-lg">
            <input className={inputCls + ' flex-1 min-w-32'} placeholder="Nom *" value={formData.name}
              onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} required />
            <input className={inputCls + ' w-28'} placeholder="Icône (ex: Hammer)" value={formData.icon}
              onChange={e => setFormData(f => ({ ...f, icon: e.target.value }))} />
            <select className={inputCls + ' w-28'} value={formData.color}
              onChange={e => setFormData(f => ({ ...f, color: e.target.value }))}>
              {COLORS.map(c => <option key={c} value={c} style={{ backgroundColor: c }}>{c}</option>)}
            </select>
            <button type="submit" className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary-dark">
              <Check size={14} />
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm">
              <X size={14} />
            </button>
          </form>
        )}

        {loading ? (
          <p className="text-sm text-gray-400 text-center py-4">Chargement...</p>
        ) : categories.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">Aucune catégorie</p>
        ) : (
          <div className="space-y-1">
            {categories.map(cat => (
              <div key={cat.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color || '#6b7280' }} />
                {editId === cat.id ? (
                  <>
                    <input className={inputCls + ' flex-1'} value={editData.name}
                      onChange={e => setEditData(f => ({ ...f, name: e.target.value }))} />
                    <input className={inputCls + ' w-28'} placeholder="Icône" value={editData.icon}
                      onChange={e => setEditData(f => ({ ...f, icon: e.target.value }))} />
                    <button onClick={() => handleUpdate(cat.id)} className="text-green-600 hover:text-green-800"><Check size={16} /></button>
                    <button onClick={() => setEditId(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm font-medium text-gray-800">{cat.name}</span>
                    {cat.icon && <span className="text-xs text-gray-400">{cat.icon}</span>}
                    <span className="text-xs text-gray-400">{cat._count?.workOrders || 0} travaux</span>
                    <button onClick={() => startEdit(cat)} className="text-gray-400 hover:text-primary"><Edit size={15} /></button>
                    <button onClick={() => handleDelete(cat.id)} className="text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card></div>
  );
};

export default WorkOrderCategoryManagement;
